import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { AuthGuard, Login } from '@lucid/auth';
import LayoutWrapper from './components/LayoutWrapper/LayoutWrapper';
import Dashboard from './pages/Dashboard/Dashboard';
import AcceptInvitation from './pages/AcceptInvitation/AcceptInvitation';
import { InvitationHandler } from './components/InvitationHandler/InvitationHandler';
import { isNavigationEnabled, isPageEnabled } from './config/featureFlags';

// Admin
import WorkspaceAccess from './pages/Admin/WorkspaceAccess/WorkspaceAccess';
import PortalSetup from './pages/Admin/PortalSetup/PortalSetup';

// Prospect Desk
import Prospects from './pages/ProspectDesk/Prospects/Prospects';
import CreateNewProspect from './pages/ProspectDesk/CreateNewProspect/CreateNewProspect';

// Order Hub
import OrderBuilder from './pages/OrderHub/OrderBuilder/OrderBuilder';
import Checkout from './pages/OrderHub/OrderBuilder/Checkout/Checkout';
import ManageOrders from './pages/OrderHub/ManageOrders/ManageOrders';
import OrderDetails from './pages/OrderHub/ManageOrders/Details/Details';

// 404
import NotFound from './pages/NotFound/NotFound';

// Define child routes as a reusable array
const appRoutes: RouteObject[] = [
  // Dashboard (always enabled)
  ...(isPageEnabled('dashboard')
    ? [
        {
          index: true,
          element: <Dashboard />,
        },
      ]
    : []),
  // Admin Panel (requires Agency-Admin-R4, Importer-Admin-R1, or Lucid Admin role)
  ...(isNavigationEnabled('admin-panel')
    ? [
        {
          path: 'admin/workspace-access',
          element: <WorkspaceAccess />,
        },
        {
          path: 'admin/portal-setup',
          element: <PortalSetup />,
        },
      ]
    : []),
  // Prospect Desk
  ...(isPageEnabled('prospectDesk')
    ? [
        {
          path: 'prospect-desk/prospects/:prospectId?/:view?',
          element: <Prospects />,
        },
        {
          path: 'prospect-desk/create-new-prospect',
          element: <CreateNewProspect />,
        },
      ]
    : []),
  // Order Hub
  ...(isPageEnabled('orderHub')
    ? [
        {
          path: 'order-hub/order-builder',
          element: <OrderBuilder />,
        },
        {
          path: 'order-hub/order-builder/checkout',
          element: <Checkout />,
        },
        {
          path: 'order-hub/manage-orders',
          element: <ManageOrders />,
        },
        {
          path: 'order-hub/manage-orders/:orderId',
          element: <OrderDetails />,
        },
      ]
    : []),
];

const routes: RouteObject[] = [
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/accept-invitation',
    element: <AcceptInvitation />,
  },
  {
    path: '/',
    element: (
      <InvitationHandler>
        <AuthGuard>
          <LayoutWrapper />
        </AuthGuard>
      </InvitationHandler>
    ),
    children: appRoutes,
  },
  // Routes with locale prefix (e.g., /en-ca, /de-de, etc.)
  {
    path: '/:locale',
    element: (
      <InvitationHandler>
        <AuthGuard>
          <LayoutWrapper />
        </AuthGuard>
      </InvitationHandler>
    ),
    children: appRoutes,
  },
  // 404 - Catch all unmatched routes
  {
    path: '*',
    element: <NotFound />,
  },
];

export const router = createBrowserRouter(routes);

export default routes;
